import React, { useMemo, useState } from "react";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import {
  buildUploadLabelMap,
  digitsOnly,
  isNonNegativeNumber,
  isValidEmail,
} from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import {
  arePhonesValid,
  isValidPhone,
  PHONE_ERROR_GENERIC,
  PHONE_ERROR_MOBILE,
} from "../../../../utils/validatePhone.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const MAX_APPLICANTS = 5;

export default function LimitedForm({
  onBack,
  onNext,
  initialPayload,
  mode = "wizard",
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("limited", productId || initialPayload?.productId));
  const initial = savedDraft?.btl?.limited || initialPayload?.btl?.limited || {};

  const [company, setCompany] = useState({
    name: initial.company?.name || "",
    regNo: initial.company?.regNo || "",
    phone: initial.company?.phone || "",
    email: initial.company?.email || "",
  });

  const [props, setProps] = useState(initial.props || "");
  const [propertyAddress, setPropertyAddress] = useState(initial.propertyAddress || "");
  const [applicants, setApplicants] = useState(
    initial.applicants?.length
      ? initial.applicants
      : [{ id: uid(), firstName: "", surname: "", phone: "", email: "" }]
  );

  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial.uploads || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const primary = applicants[0] || {};
  const canAddMore = applicants.length < MAX_APPLICANTS;
  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));

  const updateApplicant = (id, key, val) =>
    setApplicants((list) => list.map((a) => (a.id === id ? { ...a, [key]: val } : a)));

  const addApplicant = () => {
    if (applicants.length >= MAX_APPLICANTS) return;
    setApplicants((a) => [...a, { id: uid(), firstName: "", surname: "", phone: "", email: "" }]);
  };

  const removeApplicant = (id) => {
    if (applicants.length <= 1) return;
    setApplicants((list) => list.filter((a) => a.id !== id));
  };

  const payloadObj = useMemo(
    () => ({
      ...(initialPayload || {}),
      btl: {
        ...(initialPayload?.btl || {}),
        borrowerType: "Ltd Co.",
        limited: { company, applicants, uploads, propertyAddress, props },
      },
      formData: {
        ...(initialPayload?.formData || {}),
        propertyAddress,
        property_address: propertyAddress,
      },
    }),
    [initialPayload, company, applicants, uploads, propertyAddress, props]
  );

  const draftKey = getDraftStorageKey("limited", productId || initialPayload?.productId);

  const validateDetails = () => {
    const err = {};
    if (!company.name.trim()) err.company_name = "Company Name is required.";
    if (!company.regNo.trim()) err.company_regNo = "Company Registration Number is required.";
    if (!isValidPhone(company.phone, { mobileOnly: false })) err.company_phone = PHONE_ERROR_GENERIC;
    if (!company.email.trim()) err.company_email = "Company Email is required.";
    else if (!isValidEmail(company.email)) err.company_email = "Enter a valid email address.";
    if (!primary.firstName?.trim()) err.primary_firstName = "Primary applicant First Name is required.";
    if (!primary.surname?.trim()) err.primary_surname = "Primary applicant Surname is required.";
    if (!isValidPhone(primary.phone)) err.primary_phone = PHONE_ERROR_MOBILE;
    if (!primary.email?.trim()) err.primary_email = "Primary applicant Email is required.";
    else if (!isValidEmail(primary.email)) err.primary_email = "Enter a valid email address.";
    setFieldErrors(err);
    setTouched(Object.fromEntries(Object.keys(err).map((k) => [k, true])));
    return !Object.keys(err).length;
  };

  const validateProperty = () => {
    const err = {};
    if (!isNonNegativeNumber(props)) err.props = "Enter a valid number (0 or more).";
    if (!propertyAddress.trim()) err.propertyAddress = "Security Address is required.";
    setFieldErrors((e) => ({ ...e, ...err }));
    setTouched((t) => ({ ...t, props: true, propertyAddress: true }));
    return !Object.keys(err).length;
  };

  const stepErrors = useMemo(
    () => ({
      details: Object.keys(fieldErrors).some((k) =>
        ["company_name", "company_regNo", "company_phone", "company_email", "primary_firstName", "primary_surname", "primary_phone", "primary_email"].includes(k)
      ),
      property: Boolean(fieldErrors.props || fieldErrors.propertyAddress),
      documents: false,
      review: false,
    }),
    [fieldErrors]
  );

  const validateStep = async (stepIndex) => {
    if (stepIndex === 0) return validateDetails();
    if (stepIndex === 1) return validateProperty();
    return true;
  };

  const finish = (extras = {}) => {
    onNext?.({
      type: "limited",
      company,
      applicants,
      uploads,
      propertyAddress,
      props,
      payloadObj,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(), []);

  return (
    <>
      <BorrowerSteppedForm
        title="LTD. CO. & LLP REGISTRATION FORM"
        subtitle="Please complete all required fields and upload necessary documents"
        draftKey={draftKey}
        draftSnapshot={payloadObj}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        mode={mode}
        detailsContinueDisabled={!arePhonesValid([
          { value: company.phone, mobileOnly: false },
          ...applicants.map((a, i) => ({ value: a.phone, required: i === 0 })),
        ])}
        renderDetails={() => (
          <div className="space-y-5">
            <div>
              <SectionTitle>Borrower (Company)</SectionTitle>
              <p className="text-xs text-slate-500 mb-3">Applicants must hold at least 20% shareholding.</p>
              <FormFieldRow>
                <FormField
                  id="company-name"
                  label="Company Name"
                  required
                  error={fieldErrors.company_name}
                  touched={touched.company_name}
                >
                  <input
                    id="company-name"
                    className="input w-full"
                    value={company.name}
                    onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))}
                    onBlur={() => touch("company_name")}
                  />
                </FormField>
                <FormField
                  id="company-regNo"
                  label="Company Registration Number"
                  required
                  error={fieldErrors.company_regNo}
                  touched={touched.company_regNo}
                >
                  <input
                    id="company-regNo"
                    inputMode="numeric"
                    className="input w-full"
                    value={company.regNo}
                    onChange={(e) => setCompany((c) => ({ ...c, regNo: digitsOnly(e.target.value) }))}
                    onBlur={() => touch("company_regNo")}
                  />
                </FormField>
                <FormField
                  id="company-phone"
                  label="Company Telephone"
                  required
                  error={fieldErrors.company_phone}
                  touched={touched.company_phone}
                >
                  <PhoneField
                    id="company-phone"
                    value={company.phone}
                    mobileOnly={false}
                    onChange={(next) => setCompany((c) => ({ ...c, phone: next }))}
                    onBlur={() => touch("company_phone")}
                  />
                </FormField>
                <FormField
                  id="company-email"
                  label="Company Email"
                  required
                  error={fieldErrors.company_email}
                  touched={touched.company_email}
                >
                  <input
                    id="company-email"
                    type="email"
                    inputMode="email"
                    className="input w-full"
                    value={company.email}
                    onChange={(e) => setCompany((c) => ({ ...c, email: e.target.value }))}
                    onBlur={() => touch("company_email")}
                  />
                </FormField>
              </FormFieldRow>
            </div>

            <div>
              <SectionTitle>Applicants</SectionTitle>
              {applicants.map((a, idx) => (
                <div key={a.id} className="rounded-xl border border-slate-200 p-3 mb-3">
                  <div className="mb-2 flex justify-between items-center">
                    <p className="text-sm font-bold text-slate-900">Applicant {idx + 1}</p>
                    {applicants.length > 1 ? (
                      <button
                        type="button"
                        className="text-xs text-red-500"
                        onClick={() => removeApplicant(a.id)}
                        aria-label={`Remove applicant ${idx + 1}`}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <FormFieldRow>
                    <FormField
                      id={`app-${a.id}-firstName`}
                      label="First Name"
                      required={idx === 0}
                      error={idx === 0 ? fieldErrors.primary_firstName : undefined}
                      touched={idx === 0 ? touched.primary_firstName : false}
                    >
                      <input
                        id={`app-${a.id}-firstName`}
                        className="input w-full"
                        value={a.firstName}
                        onChange={(e) => updateApplicant(a.id, "firstName", e.target.value)}
                        onBlur={() => idx === 0 && touch("primary_firstName")}
                      />
                    </FormField>
                    <FormField
                      id={`app-${a.id}-surname`}
                      label="Surname"
                      required={idx === 0}
                      error={idx === 0 ? fieldErrors.primary_surname : undefined}
                      touched={idx === 0 ? touched.primary_surname : false}
                    >
                      <input
                        id={`app-${a.id}-surname`}
                        className="input w-full"
                        value={a.surname}
                        onChange={(e) => updateApplicant(a.id, "surname", e.target.value)}
                        onBlur={() => idx === 0 && touch("primary_surname")}
                      />
                    </FormField>
                    <FormField id={`app-${a.id}-phone`} label="Phone Number" required={idx === 0}>
                      <PhoneField
                        id={`app-${a.id}-phone`}
                        value={a.phone}
                        required={idx === 0}
                        onChange={(next) => updateApplicant(a.id, "phone", next)}
                        onBlur={() => idx === 0 && touch("primary_phone")}
                      />
                    </FormField>
                    <FormField
                      id={`app-${a.id}-email`}
                      label="Email"
                      required={idx === 0}
                      error={idx === 0 ? fieldErrors.primary_email : undefined}
                      touched={idx === 0 ? touched.primary_email : false}
                    >
                      <input
                        id={`app-${a.id}-email`}
                        type="email"
                        className="input w-full"
                        value={a.email}
                        onChange={(e) => updateApplicant(a.id, "email", e.target.value)}
                        onBlur={() => idx === 0 && touch("primary_email")}
                      />
                    </FormField>
                  </FormFieldRow>
                </div>
              ))}
              <button
                type="button"
                disabled={!canAddMore}
                onClick={addApplicant}
                className={`px-4 py-2 rounded-xl text-white text-sm ${
                  canAddMore ? "bg-emerald-500 hover:bg-emerald-600" : "bg-emerald-300 cursor-not-allowed"
                }`}
              >
                + Add Applicant
              </button>
            </div>
          </div>
        )}
        renderProperty={() => (
          <div>
            <SectionTitle>Property &amp; finance</SectionTitle>
            <FormFieldRow>
              <FormField
                id="limited-props"
                label="Number of properties owned"
                required
                error={fieldErrors.props}
                touched={touched.props}
              >
                <input
                  id="limited-props"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="input w-full"
                  value={props}
                  onChange={(e) => setProps(e.target.value)}
                  onBlur={() => touch("props")}
                />
              </FormField>
              <FormField
                id="limited-propertyAddress"
                label="Security Address"
                required
                error={fieldErrors.propertyAddress}
                touched={touched.propertyAddress}
                className="sm:col-span-2"
              >
                <input
                  id="limited-propertyAddress"
                  className="input w-full"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  onBlur={() => touch("propertyAddress")}
                />
              </FormField>
            </FormFieldRow>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel
            uploads={uploads}
            uploadingFiles={uploadingFiles}
            onUpload={handleUpload}
            onRemove={handleRemove}
          />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [
                  { label: "Company", value: company.name },
                  { label: "Reg. No.", value: company.regNo },
                  { label: "Company phone", value: company.phone },
                  { label: "Company email", value: company.email },
                ],
                content: applicants.map((a, i) => (
                  <p key={a.id} className="text-sm">
                    <span className="text-slate-500">Applicant {i + 1}: </span>
                    {a.firstName} {a.surname} · {a.phone} · {a.email}
                  </p>
                )),
              },
              {
                stepId: "property",
                stepIndex: 1,
                title: "Property & finance",
                rows: [
                  { label: "Properties owned", value: props },
                  { label: "Security Address", value: propertyAddress },
                ],
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
